import type { HarmonyPackageFolder } from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import { createOptimisticAction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'

import { getHarmonyFolderCollection } from '../getCollections'

const logger = useRhythmicLogger('useHarmonyPlanContext:updateFolder')

export interface UpdateHarmonyPlanFolderPayload {
  name?: string
  description?: string | null
  parentId?: string | null
}

interface UpdateContext {
  planId: string
}

function validateParams(folderId: string, payload: UpdateHarmonyPlanFolderPayload, context: UpdateContext) {
  if (!folderId) throw new Error('Folder identifier is required to update a harmony plan folder')

  if (!context.planId) throw new Error('Harmony plan identifier is required to update a folder')

  if (payload.name === undefined && payload.description === undefined && payload.parentId === undefined) {
    throw new Error('At least one field is required to update a harmony plan folder')
  }

  return { folderId, planId: context.planId }
}

export function handleUpdateHarmonyPlanFolder(
  folderId: string,
  payload: UpdateHarmonyPlanFolderPayload,
  context: UpdateContext,
): Transaction {
  const { planId } = validateParams(folderId, payload, context)
  const collection = getHarmonyFolderCollection(planId)

  const updateFolderTx = createOptimisticAction<{
    folderId: string
    payload: UpdateHarmonyPlanFolderPayload
  }>({
    onMutate: (mutation) => {
      collection.update(mutation.folderId, (draft: HarmonyPackageFolder) => {
        if (mutation.payload.name !== undefined) draft.name = mutation.payload.name
        if (mutation.payload.description !== undefined) draft.description = mutation.payload.description
        if (mutation.payload.parentId !== undefined) draft.parent_id = mutation.payload.parentId
        draft.updated_at = new Date()
      })
    },
    mutationFn: async (mutation) => {
      const body: Record<string, unknown> = {}
      if (mutation.payload.name !== undefined) body.name = mutation.payload.name
      if (mutation.payload.description !== undefined) body.description = mutation.payload.description
      if (mutation.payload.parentId !== undefined) body.parent_id = mutation.payload.parentId

      logger.debug('Persisting harmony plan folder update via API', {
        folderId: mutation.folderId,
        body,
      })
      const result = await $fetch<{ txid?: string | number }>(`/api/harmony-package-folders/${mutation.folderId}`, {
        method: 'PUT',
        body,
      })

      if (result?.txid && collection.utils.awaitTxId) {
        await collection.utils.awaitTxId(Number.parseInt(result.txid.toString()))
      }
    },
  })

  return updateFolderTx({ folderId, payload })
}
